import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Organization, OrganizationForm, OrganizationSearchForm } from 'entities/organization'

type OrganizationsResponse = PagingResponse & {
    results: Organization[]
}

export function useFetchOrganizationsApi(searchForm?: Form<OrganizationSearchForm>): IndexApiSet<OrganizationsResponse> & { execute: () => void } {
    const apiPath = 'organizations/'
    const api = useIndexApi<OrganizationsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvOrganizationsApi(searchForm: Form<OrganizationSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'organizations/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'organization', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostOrganizationApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrganizationForm>) => void } {
    const apiSet = usePostApi<BaseResponse, OrganizationForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<OrganizationForm>) => {
        const apiPath = `organizations/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutOrganizationApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrganizationForm>) => void } {
    const apiSet = usePutApi<BaseResponse, OrganizationForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<OrganizationForm>) => {
        const apiPath = `organizations/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutOrganizationsApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrganizationForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, OrganizationForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrganizationForm[]>) => {
        const apiPath = `organizations/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteOrganizationApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `organizations/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type IssueRequestForm = {
    amount?: number
}

export function useIssueRequestFormApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<IssueRequestForm>) => void } {
    const apiSet = usePostApi<BaseResponse, IssueRequestForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number, form: Form<IssueRequestForm>) => {
        const apiPath = `organizations/${id}/issue_request_form/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useFetchPrintRequestOrganizationsApi(): IndexApiSet<OrganizationsResponse> & { execute: () => void } {
    const apiPath = 'print_requests/'
    const api = useIndexApi<OrganizationsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
